<template>
  <SettingEditLayout
    ref="layout"
    title="Slack連携・通知"
    summary="通知の種類ごとに、オン・オフと通知先を決めます。"
    :fields="fields"
    :validate="validate"
  >
    <template #default="{ form }">
      <!--
        **契約で使えない会社には、入れさせない。**
        入れられる画面を出しておいて保存で断るより、
        はじめから触れないほうが親切。
      -->
      <CAlert v-if="!slackAvailable" color="warning">
        現在のご契約や設定では、Slack 通知をご利用いただけません。
        設定は変更できますが、通知は送信されません。
      </CAlert>

      <section class="notice-block">
        <h3>Slack接続情報</h3>
        <label for="slack-token">Botトークン</label>
        <CInput
          id="slack-token"
          v-model.trim="form.slack_token"
          type="password"
          autocomplete="new-password"
          :description="tokenDescription"
        />
        <label class="switch-card" for="clear-token">
          <input id="clear-token" type="checkbox" v-model="form.clear_slack_token"/>
          <span>
            <strong>保存済みトークンを削除する</strong>
            <small>すべての通知をオフにした場合のみ削除できます。</small>
          </span>
        </label>
      </section>

      <section
        v-for="notice in notices"
        :key="notice.flag"
        class="notice-block"
      >
        <h3>{{ notice.title }}</h3>
        <p class="notice-block__desc">{{ notice.description }}</p>

        <label class="switch-card" :for="notice.flag">
          <input :id="notice.flag" type="checkbox" v-model="form[notice.flag]"/>
          <span>
            <strong>{{ form[notice.flag] ? 'オン' : 'オフ' }}</strong>
            <small>{{ notice.switchNote }}</small>
          </span>
        </label>

        <template v-if="form[notice.flag]">
          <label :for="notice.channel">通知先チャンネルID</label>
          <CInput
            :id="notice.channel"
            v-model.trim="form[notice.channel]"
            maxlength="50"
            :description="notice.channelNote"
          />
        </template>
      </section>
    </template>
  </SettingEditLayout>
</template>

<script>
import SettingEditLayout from '../SettingEditLayout.vue'
import { findSection } from '../settingSections'

/**
 * 通知の設定だけを直す画面。
 *
 * <h2>36協定の通知はここに出さない</h2>
 *
 * 労務法令の判定を出していないあいだ、その通知の設定は<b>画面に出さない。</b>
 * 直しても1通も送られないものを促すと、次に本当に届かなくなったときに
 * もう読まれない。
 *
 * 出さないだけで、<b>値は保存のときそのまま送り返す</b>
 * （SettingEditLayout が読み込んだ全項目を土台にする）。
 * 空で上書きすると、出すようになったときに会社が決めていた通知先が消える。
 */
export default {
  name: 'NotificationEdit',
  components: { SettingEditLayout },

  data () {
    return {
      fields: findSection('notification').fields,
      notices: [
        {
          title: '申請状況の即時通知',
          description: '申請・承認・差戻しの実行時に即時送信されます。',
          flag: 'slack_notification_flg',
          channel: 'slack_channel',
          switchNote: '申請が動いたときに送ります。',
          channelNote: '例: C01234567'
        },
        {
          title: '承認待ちリマインド',
          description: '会社の営業日に1日1回、承認待ちが残っていることを送ります。',
          flag: 'pending_approval_notification_flg',
          channel: 'pending_approval_slack_channel',
          switchNote: '送信時刻は「就業時間」の設定に従います。',
          channelNote: '例: C01234567'
        },
        {
          title: '管理部門向け朝通知',
          description: '当日の休暇予定を朝にまとめて送信します。',
          flag: 'management_daily_notification_flg',
          channel: 'management_daily_slack_channel',
          switchNote: '送信時刻は「就業時間」の設定に従います。',
          channelNote: '例: C01234567'
        }
      ]
    }
  },

  computed: {
    /** 契約で Slack を使えるか。**サーバが返す値をそのまま見る。** */
    slackAvailable () {
      const layout = this.$refs.layout
      return layout ? Boolean(layout.received.slack_feature_enabled) : true
    },
    tokenDescription () {
      const layout = this.$refs.layout
      const configured = layout && layout.received.slack_token_configured
      return configured
        ? '設定済みです。変更する場合のみ新しい値を入力してください。'
        : 'まだ登録されていません。'
    }
  },

  methods: {
    validate (form) {
      for (const notice of this.notices) {
        if (!form[notice.flag]) continue
        const channel = String(form[notice.channel] || '').trim()
        if (!channel) {
          return notice.title + 'をオンにする場合は、通知先チャンネルIDを入力してください。'
        }
        if (channel.length > 50) {
          return notice.title + 'の通知先チャンネルIDは50文字以内で入力してください。'
        }
      }
      // **通知が1つでもオンなら、トークンを消せない。**
      // 消すと、オンのまま1通も届かない状態になる。
      const anyOn = this.notices.some(notice => form[notice.flag])
      if (form.clear_slack_token && anyOn) {
        return 'トークンを削除する場合は、先にすべての通知をオフにしてください。'
      }
      return ''
    }
  }
}
</script>

<style scoped>
.notice-block {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}

.notice-block:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.notice-block h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.notice-block__desc {
  margin: 0 0 0.75rem;
  color: #5c6873;
  font-size: 0.875rem;
}

label { display: block; font-weight: 600; }

.switch-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  margin: 0.5rem 0;
  border: 1px solid #d8dbe0;
  border-radius: 0.35rem;
  cursor: pointer;
  font-weight: 400;
}

.switch-card strong { display: block; }
.switch-card small { color: #5c6873; }
</style>
